"use client";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TextAreaProps } from "@nextui-org/react";
import { Spinner, Textarea, Input, Modal, ModalContent, ModalHeader, ModalBody } from "@nextui-org/react";
import { cn } from "./cn";
import { ID_CONSTANTS, isCompactApplicationType, promptInputType, UploadedFileType } from '../utils/constants';
import { CtyButton } from '../button/CtyButton';
import { Icon } from '@iconify/react';
import { useSelector } from 'react-redux';
import {
  Advisor,
  RootState,
  SETTING_TYPE,
  updateCurrentChatMentionedEntities,
  useAppDispatch,
  useGetAiWorkersByCategoryQuery,
  categoryType,
  useGetAiWorkersBySourceTypeQuery,
  usePostOfferMutation,
  usePostWebRtcSessionMutation,
  setConversationRegionSelection,
} from '@circuitry-ai/doc-data';
import { SearchableDropdown } from '../utiluicomponents/searchabledropdown';
import { extractCommandJsonFromAttrs } from '../utiluicomponents/CommandFormComponents';
import { SpeechUtility } from '../utils/speechUtils';
import {
  buildCountryOptionsMap,
  buildConversationRegionSelection,
  buildLanguageOptionsForRegion,
  getConversationRegions,
  getCountryConverseState,
  resolveDefaultCountryAndLanguage,
  resolveLanguageSelection,
} from '../utils/conversationRegionUtils';
import { filterShowingPalAgents, formatFileSize, generateCircuitryLogoSvgBlobUrl, getFileType } from '../utils/commonutils';
import {
  ATTACHMENT_FILE_INPUT_ACCEPT,
  AttachmentPreviewSection,
  filterFilesByAttachmentSizeLimit,
  ATTACHMENT_SIZE_TOAST_IMAGE_VIDEO,
  ATTACHMENT_SIZE_TOAST_FILE_DOCUMENT,
} from './attachmentUploadShared';
import PromptActionsRadialMenu, { type PromptRadialAction } from './PromptActionsRadialMenu';
import MicrophoneWave from '../utiluicomponents/Microphonewave';
import { useScreenWidth } from '../utiluicomponents/useScreenWidth';
import { FileScanner } from '../scanner/filescanner';
import Webcam from 'react-webcam';
import { useTranslation } from 'react-i18next';
import { checkType, CtyShowToast } from '../toast/CtyToast';
import {
  cleanupVoiceAgentWebRtc,
  startVoiceAgentWebRtcSession,
} from '../voiceagent/voiceAgentWebRtc';

function base64DataUrlToFile(dataUrl: string, filename = 'photo.jpg'): File {
  const byteString = atob(dataUrl.split(',')[1]);
  const mime = 'image/jpeg';
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
  return new File([ab], filename, { type: mime });
}

function filesFromClipboardData(data: DataTransfer | null): File[] {
  if (!data) return [];
  if (data.files?.length) {
    return Array.from(data.files);
  }
  const out: File[] = [];
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind === 'file') {
      const f = item.getAsFile();
      if (f) out.push(f);
    }
  }
  return out;
}

interface CtyPromptInputProps extends TextAreaProps {
  inputHeight?: string;
  enableTranslation?: boolean;
  enableSpeak?: boolean;
  enableImage?: boolean;
  isSpeechMode?: boolean;
  enableFile?: boolean;
  enableSendBtn?: boolean;
  enableSearch?: boolean;
  setIsSpeechMode?: (value: boolean) => void;
  handleSendClick: (userInput: string, imageMeta?: { sources: { source_id: string; file_name: string }[] }, attachedImageFiles?: File[]) => void;
  triggerEveryInputChange?: boolean;
  elementType: promptInputType;
  enableMention?: boolean;
  enableLiveChat?: boolean;
  clearInputOnSend?: boolean;
  isSearchSpinnerLoading?: boolean;
  uploadedFiles?: UploadedFileType[];
  setUploadedFiles?: React.Dispatch<React.SetStateAction<UploadedFileType[]>>;
  canSendEmptyMessage?: boolean;
  enableActionMention?: boolean;
  prefillText?: string;
  onFilesSelected?: (files: File[]) => void | Promise<void>;
  /** Fired when WebRTC voice agent connection becomes connected or disconnects (advisor chat transcript overlay). */
  onVoiceAgentConnectionChange?: (connected: boolean) => void;
  /** Each parsed line from the voice agent pipecat data channel. */
  onVoiceAgentTranscriptLine?: (line: { role: 'user' | 'assistant'; text: string }) => void;
  /** When true, show country/language region dropdowns (converse screen only). */
  enableConversationRegionSelection?: boolean;
  /**
   * PAL view (same flag the advisors list uses): restricts the `/` agent list to agents whose
   * Deploy tab enabled `metadata.showingPal`.
   */
  palView?: boolean;
  /**
   * When true on mobile / widget / extension, forces optional actions into a radial menu.
   * Ignored on desktop web. When omitted on those layouts, radial menu is used if count > 1.
   * Send always stays pinned beside the input.
   */
  stackActionsBelowInput?: boolean;
}

interface MentionAdvisor {
  advisor_id: string;
  name: string;
}

interface MentionAgent {
  aiworker_id: string;
  name: string;
  socketUrl?: string;
  commandJson?: Record<string, any>;
}

type MentionDropdownSetters = {
  setMentionType: React.Dispatch<React.SetStateAction<'advisor' | 'agent' | null>>;
  setShowAdvisorDropdown: (open: boolean) => void;
  setShowAgentDropdown: (open: boolean) => void;
  setFilteredAdvisors: (advisors: Advisor[]) => void;
  setFilteredActions: (actions: any[]) => void;
  setFilteredAgents: (agents: any[]) => void;
  setMentionIndex: (idx: number | null) => void;
  setAgentMentionIndex: (idx: number | null) => void;
};

function resetMentionDropdowns(s: MentionDropdownSetters) {
  s.setShowAdvisorDropdown(false);
  s.setShowAgentDropdown(false);
  s.setMentionIndex(null);
  s.setAgentMentionIndex(null);
  s.setMentionType(null);
}

type MentionSyncContext = {
  enableMention: boolean;
  token: string | undefined;
  enableActionMention: boolean;
  allAdvisors: Advisor[];
  selectedAdvisor: MentionAdvisor | null;
  actionsList: any;
  mentionAgents: any[];
  selectedAgent: MentionAgent | null;
};

function advisorsMatchingQuery(allAdvisors: Advisor[], mentionQuery: string, excludeId?: string): Advisor[] {
  const base = allAdvisors.filter((a) => a.advisor_id !== excludeId);
  if (mentionQuery.trim() === '') return base;
  const q = mentionQuery.toLowerCase();
  return base.filter((a) => a.name.toLowerCase().includes(q));
}

function actionsMatchingQuery(actionsList: any, mentionQuery: string, enableActionMention: boolean): any[] {
  if (!enableActionMention || !actionsList) return [];
  if (mentionQuery.trim() === '') return actionsList;
  return actionsList.filter((action: any) => action.name.toLowerCase().includes(mentionQuery.toLowerCase()));
}

function agentsMatchingQuery(allAgents: any[], mentionQuery: string, excludeId?: string): any[] {
  const base = allAgents.filter((agent: any) => agent.aiworker_id !== excludeId);
  if (mentionQuery.trim() === '') return base;
  const q = mentionQuery.toLowerCase();
  return base.filter((agent: any) => agent.name.toLowerCase().includes(q));
}

function applyAtMentionDropdowns(value: string, lastAtIndex: number, ctx: MentionSyncContext, s: MentionDropdownSetters) {
  const mentionQuery = value.slice(lastAtIndex + 1);
  s.setMentionType('advisor');
  s.setShowAgentDropdown(false);

  const availableAdvisors = advisorsMatchingQuery(ctx.allAdvisors, mentionQuery, ctx.selectedAdvisor?.advisor_id);
  s.setFilteredAdvisors(availableAdvisors);

  const availableActions = actionsMatchingQuery(ctx.actionsList, mentionQuery, ctx.enableActionMention);
  s.setFilteredActions(availableActions);

  s.setShowAdvisorDropdown(availableAdvisors.length > 0 || availableActions.length > 0);
  s.setMentionIndex(lastAtIndex);
}

function applySlashMentionDropdowns(value: string, lastSlashIndex: number, ctx: MentionSyncContext, s: MentionDropdownSetters) {
  const mentionQuery = value.slice(lastSlashIndex + 1);
  s.setMentionType('agent');
  s.setShowAdvisorDropdown(false);

  const specialAgents = [
    {
      aiworker_id: 'agent_log',
      name: 'Agent History',
      description: 'Query claim information by claim number',
      type: 'special',
    },
  ];
  const allAgents = [...specialAgents, ...ctx.mentionAgents];
  if (allAgents.length === 0) return;

  const list = agentsMatchingQuery(allAgents, mentionQuery, ctx.selectedAgent?.aiworker_id);
  s.setFilteredAgents(list);
  s.setShowAgentDropdown(list.length > 0);
  s.setAgentMentionIndex(lastSlashIndex);
}

function syncMentionDropdownsForInput(value: string, ctx: MentionSyncContext, s: MentionDropdownSetters) {
  if (!ctx.enableMention || !ctx.token) {
    resetMentionDropdowns(s);
    return;
  }

  const lastAtIndex = value.lastIndexOf('@');
  const lastSlashIndex = value.lastIndexOf('/');
  const atWins = lastAtIndex > lastSlashIndex && lastAtIndex !== -1;
  const slashWins = lastSlashIndex > lastAtIndex && lastSlashIndex !== -1 && ctx.token;

  if (atWins) {
    applyAtMentionDropdowns(value, lastAtIndex, ctx, s);
    return;
  }
  if (slashWins) {
    applySlashMentionDropdowns(value, lastSlashIndex, ctx, s);
    return;
  }

  resetMentionDropdowns(s);
}

type PromptCameraModalFooterProps = Readonly<{
  cameraMode: 'photo' | 'video';
  hasCapturedMedia: boolean;
  isRecordingVideo: boolean;
  t: (key: string) => string;
  onRetake: () => void;
  onUpload: () => void;
  onCapturePhoto: () => void;
  onToggleVideoRecord: () => void;
}>;

function PromptCameraModalFooter(props: PromptCameraModalFooterProps) {
  const {
    cameraMode,
    hasCapturedMedia,
    isRecordingVideo,
    t,
    onRetake,
    onUpload,
    onCapturePhoto,
    onToggleVideoRecord,
  } = props;

  const retakeUploadButtons = (
    <div className="flex items-center gap-2">
      <CtyButton color="default" variant="flat" size="sm" onPress={onRetake}>
        {t('Retake')}
      </CtyButton>
      <CtyButton color="primary" variant="flat" size="sm" onPress={onUpload}>
        {t('Upload')}
      </CtyButton>
    </div>
  );

  if (cameraMode === 'photo') {
    if (hasCapturedMedia) {
      return retakeUploadButtons;
    }
    return (
      <CtyButton color="primary" variant="flat" size="sm" onPress={onCapturePhoto} startContent={<Icon icon="mdi:camera" width={20} height={20} />}>
        {t('Capture')}
      </CtyButton>
    );
  }

  if (hasCapturedMedia) {
    return retakeUploadButtons;
  }

  return (
    <CtyButton
      color={isRecordingVideo ? 'danger' : 'primary'}
      variant="flat"
      size="sm"
      onPress={onToggleVideoRecord}
      startContent={<Icon icon={isRecordingVideo ? 'mdi:stop-circle-outline' : 'mdi:video'} width={20} height={20} />}
    >
      {isRecordingVideo ? t('Stop') : t('Record')}
    </CtyButton>
  );
}

const CtyPromptInput = React.forwardRef<HTMLTextAreaElement, CtyPromptInputProps>(
  ({ classNames = {}, ...props }, ref) => {

    const { inputHeight, handleSendClick, enableImage = false, enableSendBtn = false, enableTranslation = false, enableSpeak = false, enableSearch = false, isSpeechMode = false, setIsSpeechMode, triggerEveryInputChange = false, elementType, enableMention = false, enableLiveChat = false, clearInputOnSend = true, isSearchSpinnerLoading = false, uploadedFiles = [], setUploadedFiles, canSendEmptyMessage = false, enableFile = false, enableActionMention = false, prefillText = '', onFilesSelected, onVoiceAgentConnectionChange, onVoiceAgentTranscriptLine, enableConversationRegionSelection = false, stackActionsBelowInput, palView = false } = props;
    const allowImageAttachment = enableImage || enableFile;
    const isCompactLayout = isCompactApplicationType();
    const screenWidth = useScreenWidth();
    const isMobileViewport = screenWidth < 768;
    // Stack on widget/extension and mobile web; keep desktop web inline.
    const useStackedPromptLayout = isCompactLayout || isMobileViewport;
    const promptRootRef = useRef<HTMLDivElement>(null);
    const { isSocketLoading, promptMessage } = useSelector((state: RootState) => state.chatAdvisorSliceReducer);

    const { token } = useSelector((state: RootState) => state.auth);
    const [postWebRtcSession] = usePostWebRtcSessionMutation();
    const [postOffer] = usePostOfferMutation();
    const [inputValue, setInputValue] = useState<string>(prefillText);
    const [isListening, setIsListening] = useState<boolean>(false);
    const [isVoiceAgentConnecting, setIsVoiceAgentConnecting] = useState(false);
    const [isVoiceAgentConnected, setIsVoiceAgentConnected] = useState(false);
    const voiceAgentPcRef = useRef<RTCPeerConnection | null>(null);
    const voiceAgentAudioRef = useRef<HTMLAudioElement | null>(null);
    const voiceAgentLocalStreamRef = useRef<MediaStream | null>(null);
    const [selectedKey, setSelectedKey] = useState<string>(SpeechUtility.getLanguage() ?? "en-US");
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
    const [radialSelectionPanel, setRadialSelectionPanel] = useState<'country' | 'language' | null>(null);
    const dispatch = useAppDispatch();
    const { allAdvisors, guestToken, advisorAbilities } = useSelector((state: RootState) => state.advisor);
    const [filteredAdvisors, setFilteredAdvisors] = useState<Advisor[]>([]);
    const [showAdvisorDropdown, setShowAdvisorDropdown] = useState(false);
    const [mentionIndex, setMentionIndex] = useState<number | null>(null);
    const defaultAdvisorLogo = generateCircuitryLogoSvgBlobUrl('#d4007e');
    const [selectedAdvisor, setSelectedAdvisor] = useState<MentionAdvisor | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const agentPayLoad = {
      category: categoryType.AGENT,
      ...(guestToken && { token: guestToken })
    }

    const { data: agentsData } = useGetAiWorkersByCategoryQuery(agentPayLoad, {
      skip: !token || !enableMention,
      refetchOnMountOrArgChange: true
    });

    // PAL only offers agents that were explicitly enabled for it on the agent's Deploy tab.
    const mentionAgents = useMemo(() => {
      const agents = Array.isArray(agentsData)
        ? agentsData
        : (agentsData as { agents?: any[] } | undefined)?.agents ?? [];
      return palView ? filterShowingPalAgents(agents) : agents;
    }, [agentsData, palView]);

    const { data: actionsList } = useGetAiWorkersBySourceTypeQuery('action', { skip: !(token && (enableImage || enableActionMention)), refetchOnMountOrArgChange: true });

    const [filteredAgents, setFilteredAgents] = useState<any[]>([]);
    const [filteredActions, setFilteredActions] = useState<any[]>([]);
    const [showAgentDropdown, setShowAgentDropdown] = useState(false);
    const [agentMentionIndex, setAgentMentionIndex] = useState<number | null>(null);
    const [selectedAgent, setSelectedAgent] = useState<MentionAgent | null>(null);
    const [mentionType, setMentionType] = useState<'advisor' | 'agent' | null>(null);
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setShowAdvisorDropdown(false);
          setShowAgentDropdown(false);
        }
      };

      if (showAdvisorDropdown || showAgentDropdown) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showAdvisorDropdown, showAgentDropdown]);

    useEffect(() => {
      // Update mentions based on selected advisor or agent
      const mentionedEntity = selectedAdvisor || selectedAgent;
      dispatch(updateCurrentChatMentionedEntities(mentionedEntity));
    }, [selectedAdvisor, selectedAgent]);

    // Update input value when prefillText changes
    useEffect(() => {
      if (prefillText) {
        setInputValue(prefillText);
      }
    }, [prefillText]);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isCameraOpen, setCameraOpen] = useState(false);
    const [cameraMode, setCameraMode] = useState<'photo' | 'video'>('photo');
    const [isRecordingVideo, setIsRecordingVideo] = useState(false);
    const [recordingSeconds, setRecordingSeconds] = useState(0);
    const [capturedPhotoDataUrl, setCapturedPhotoDataUrl] = useState<string | null>(null);
    const [capturedVideoFile, setCapturedVideoFile] = useState<File | null>(null);
    const [capturedVideoUrl, setCapturedVideoUrl] = useState<string | null>(null);
    const webcamRef = useRef<Webcam>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordedChunksRef = useRef<BlobPart[]>([]);
    const { t } = useTranslation();

    /** Same metadata object as translation's `selected_languages`; voice branch only if `selected_voice_agent` key is present. */
    const { voiceAgentAssistantId, useVoiceAgentMic } = useMemo(() => {
      const meta = advisorAbilities?.[SETTING_TYPE.SPEAK]?.metadata;
      const hasKey =
        meta != null &&
        typeof meta === 'object' &&
        Object.prototype.hasOwnProperty.call(meta, 'selected_voice_agent');
      const id = typeof meta?.selected_voice_agent === 'string' ? meta.selected_voice_agent.trim() : '';
      return { voiceAgentAssistantId: id, useVoiceAgentMic: hasKey };
    }, [advisorAbilities]);

    const { enableCountry, regions } = useMemo(
      () => getCountryConverseState(advisorAbilities),
      [advisorAbilities]
    );

    const conversationRegions = useMemo(
      () => getConversationRegions(regions),
      [regions]
    );

    const countryOptionsMap = useMemo(
      () => buildCountryOptionsMap(regions),
      [regions]
    );

    const selectedRegion = useMemo(
      () => conversationRegions.find((region) => region.country_code === selectedCountryCode),
      [conversationRegions, selectedCountryCode]
    );

    const regionLanguageMap = useMemo(
      () => buildLanguageOptionsForRegion(selectedRegion),
      [selectedRegion]
    );

    const showLegacyLanguageDropdown = enableConversationRegionSelection && !enableCountry && enableTranslation;
    const showRegionLanguageDropdown = enableConversationRegionSelection && enableCountry;
    const inputDisabled = Boolean(props.isDisabled);

    const hasCapturedMedia = cameraMode === 'photo' ? !!capturedPhotoDataUrl : !!capturedVideoFile;
    const formatRecordingTime = (totalSeconds: number) => {
      const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const ss = String(totalSeconds % 60).padStart(2, '0');
      return `${mm}:${ss}`;
    };

    const processSelectedFiles = (filesArray: File[]) => {
      if (filesArray.length === 0) return;
      const { accepted, rejectedImageVideo, rejectedFileDocument } =
        filterFilesByAttachmentSizeLimit(filesArray);
      if (rejectedImageVideo) {
        CtyShowToast(checkType.error, ATTACHMENT_SIZE_TOAST_IMAGE_VIDEO);
      }
      if (rejectedFileDocument) {
        CtyShowToast(checkType.error, ATTACHMENT_SIZE_TOAST_FILE_DOCUMENT);
      }
      if (accepted.length === 0) return;
      if (onFilesSelected) {
        onFilesSelected(accepted);
      } else {
        const filesWithDetails = accepted.map((file: File) => ({
          size: formatFileSize(file.size),
          name: file.name,
          type: getFileType(file.name).toUpperCase(),
          uploading: true,
          file,
        }));
        setUploadedFiles?.((prev) => [...prev, ...filesWithDetails]);
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (!allowImageAttachment || isSocketLoading) return;
      const raw = filesFromClipboardData(e.clipboardData);
      const filesArray = enableFile ? raw : raw.filter((f: File) => f.type?.startsWith?.('image/'));
      if (filesArray.length === 0) return;
      e.preventDefault();
      processSelectedFiles(filesArray);
    };

    const handleDragOver = (e: React.DragEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (!allowImageAttachment || isSocketLoading) return;
      const hasFiles = Array.from(e.dataTransfer?.types ?? []).includes('Files');
      if (hasFiles) e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (!allowImageAttachment || isSocketLoading) return;
      const raw = Array.from(e.dataTransfer?.files ?? []);
      const filesArray = enableFile ? raw : raw.filter((f: File) => f.type?.startsWith?.('image/'));
      if (filesArray.length === 0) return;
      e.preventDefault();
      processSelectedFiles(filesArray);
    };

    const handleSubmit = (e?: React.FormEvent) => {
      e?.preventDefault();
      if (inputDisabled) return;
      const hasAttachmentsToSend = allowImageAttachment && uploadedFiles.some((f) => f.source_id);
      if (!canSendEmptyMessage && !inputValue?.trim() && !hasAttachmentsToSend) return;
      if (uploadedFiles.some(file => file.uploading)) return;
      const readyFiles = uploadedFiles.filter((f) => f.source_id);
      const imageMeta = readyFiles.length > 0
        ? { sources: readyFiles.map((f) => ({ source_id: f.source_id as string, file_name: f.name })) }
        : undefined;
      const attachedImageFiles = readyFiles.length > 0 ? readyFiles.map((f) => f.file) : undefined;
      handleSendClick(inputValue || '', imageMeta, attachedImageFiles);
      clearInputOnSend && setInputValue('');
      clearInputOnSend && setUploadedFiles?.([]);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);

      syncMentionDropdownsForInput(
        value,
        {
          enableMention,
          token,
          enableActionMention,
          allAdvisors,
          selectedAdvisor,
          actionsList,
          mentionAgents,
          selectedAgent,
        },
        {
          setMentionType,
          setShowAdvisorDropdown,
          setShowAgentDropdown,
          setFilteredAdvisors,
          setFilteredActions,
          setFilteredAgents,
          setMentionIndex,
          setAgentMentionIndex,
        }
      );

      if (triggerEveryInputChange) {
        handleSendClick(value || '');
      }
    };

    const handleBrowseFiles = () => {
      fileInputRef.current?.click();
    }

    const handleAdvisorSelect = (advisor: MentionAdvisor) => {
      if (mentionIndex === null) return;

      const beforeMention = inputValue.slice(0, mentionIndex);
      const afterMention = inputValue.slice(mentionIndex).split(" ").slice(1).join(" ");

      const newValue = `${beforeMention}${afterMention}`.trim(); // Remove mention text

      setInputValue(newValue);
      setShowAdvisorDropdown(false);
      setMentionIndex(null);

      // Set only a single advisor and clear agent selection
      setSelectedAdvisor(advisor);
      setSelectedAgent(null);
      dispatch(updateCurrentChatMentionedEntities(advisor)); // Store it in Redux
    };

    const handleActionSelect = (action: any) => {
      if (mentionIndex === null) return;
      // For Actions, we want to KEEP the text but autocomplete the name?
      // Actually, standard behavior in this app is to remove text and show pill.
      // However, UXGenie needs the Input Value.
      // But if we use Pill, inputValue excludes the name.
      // UXGenie handleSendClick receives inputValue.
      // Let's stick to the requested behavior: "available for @ command".
      // If I select it, I'll autocomplete the TEXT, not use a pill. (Simpler integration for UXGenie regex)

      const beforeMention = inputValue.slice(0, mentionIndex);
      const mentionPart = inputValue.slice(mentionIndex).split(" ");
      const afterMention = mentionPart.length > 1 ? mentionPart.slice(1).join(" ") : "";

      // Autocomplete to: @ActionName [cursor]
      const newValue = `${beforeMention}@${action.name} ${afterMention}`;

      setInputValue(newValue);
      setShowAdvisorDropdown(false);
      setMentionIndex(null);

      // Focus back on input? (Ref handling omitted for brevity, user acts naturally)
    };

    const handleAgentSelect = (agent: any) => {
      if (agentMentionIndex === null) return;

      const beforeMention = inputValue.slice(0, agentMentionIndex);
      const afterMention = inputValue.slice(agentMentionIndex).split(" ").slice(1).join(" ");

      const newValue = `${beforeMention}${afterMention}`.trim(); // Remove mention text

      setInputValue(newValue);
      setShowAgentDropdown(false);
      setAgentMentionIndex(null);

      let agentWebSocketUrl: string | undefined;
      if (Array.isArray(agentsData)) {
        agentWebSocketUrl = undefined;
      } else if (agentsData && typeof agentsData === 'object' && 'agent_websocket_url' in agentsData) {
        agentWebSocketUrl = (agentsData as { agent_websocket_url?: string }).agent_websocket_url ?? undefined;
      } else {
        agentWebSocketUrl = undefined;
      }

      const agentWithSocketUrl: MentionAgent = {
        aiworker_id: agent.aiworker_id,
        name: agent.name,
        socketUrl: agentWebSocketUrl,
        commandJson: extractCommandJsonFromAttrs(agent?.aiworkers_attrs),
      };

      setSelectedAgent(agentWithSocketUrl);
      setSelectedAdvisor(null);
      dispatch(updateCurrentChatMentionedEntities(agentWithSocketUrl));
    };

    const uploadFileHandler = (e: any) => {
      const target = e.target as HTMLInputElement;
      const files = target.files;
      if (files) {
        const all = Array.from(files);
        const filesArray = enableFile ? all : all.filter((f: File) => f.type?.startsWith?.('image/'));
        if (filesArray.length === 0) {
          target.value = '';
          return;
        }
        processSelectedFiles(filesArray);
        target.value = '';
      }
    };

    const handleCameraCapture = () => {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (imageSrc) {
        setCapturedPhotoDataUrl(imageSrc);
      }
    };

    const closeCameraModal = () => {
      const recorder = mediaRecorderRef.current;
      if (isRecordingVideo && recorder && recorder.state !== 'inactive') {
        recorder.stop();
      }
      setIsRecordingVideo(false);
      setRecordingSeconds(0);
      setCapturedPhotoDataUrl(null);
      setCapturedVideoFile(null);
      setCapturedVideoUrl(null);
      setCameraOpen(false);
    };

    const handleVideoRecordToggle = () => {
      if (isRecordingVideo) {
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
          recorder.stop();
        }
        return;
      }

      const stream = webcamRef.current?.video?.srcObject as MediaStream | null;
      if (!stream) return;

      const preferred = 'video/webm;codecs=vp9,opus';
      const fallback = 'video/webm';
      const mimeType = MediaRecorder.isTypeSupported(preferred) ? preferred : fallback;

      recordedChunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const chunks = recordedChunksRef.current;
        recordedChunksRef.current = [];
        setIsRecordingVideo(false);
        setRecordingSeconds(0);
        if (chunks.length === 0) return;
        const normalizedMimeType = (mimeType || 'video/webm').split(';')[0].trim() || 'video/webm';
        const blob = new Blob(chunks, { type: normalizedMimeType });
        const file = new File([blob], `video-${Date.now()}.webm`, { type: normalizedMimeType });
        setCapturedVideoFile(file);
      };

      recorder.start();
      setIsRecordingVideo(true);
    };

    const handleCapturedUpload = () => {
      if (cameraMode === 'photo' && capturedPhotoDataUrl) {
        processSelectedFiles([base64DataUrlToFile(capturedPhotoDataUrl)]);
        closeCameraModal();
        return;
      }
      if (cameraMode === 'video' && capturedVideoFile) {
        processSelectedFiles([capturedVideoFile]);
        closeCameraModal();
      }
    };

    const handleRetakeCapture = () => {
      if (cameraMode === 'photo') {
        setCapturedPhotoDataUrl(null);
      } else {
        setCapturedVideoFile(null);
        setCapturedVideoUrl(null);
      }
    };

    useEffect(() => {
      return () => {
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
          recorder.stop();
        }
      };
    }, []);

    useEffect(() => {
      if (!isRecordingVideo) return;
      const timer = globalThis.setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
      return () => globalThis.clearInterval(timer);
    }, [isRecordingVideo]);

    useEffect(() => {
      if (!capturedVideoFile) {
        setCapturedVideoUrl(null);
        return;
      }
      const url = URL.createObjectURL(capturedVideoFile);
      setCapturedVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    }, [capturedVideoFile]);

    useEffect(() => {
      if (isSpeechMode && !isListening && inputValue?.trim().length > 0 && !inputDisabled) {
        handleSendClick(inputValue || '');
        setInputValue('');
      }
    }, [isListening]);

    useEffect(() => {
      setInputValue(promptMessage);
    }, [promptMessage]);

    const handleDropDown = (key: string) => {
      setSelectedKey(key);
      if (showRegionLanguageDropdown && selectedCountryCode) {
        setConversationRegionSelection(
          buildConversationRegionSelection(
            selectedCountryCode,
            key,
            countryOptionsMap,
            regionLanguageMap
          )
        );
      }
      SpeechUtility.destroyInstance();
      SpeechUtility.setLanguage(key);
    };

    const handleCountryDropDown = (countryCode: string) => {
      setSelectedCountryCode(countryCode);
      const region = conversationRegions.find((entry) => entry.country_code === countryCode);
      const languageMap = buildLanguageOptionsForRegion(region);
      const nextLanguage = resolveLanguageSelection(selectedKey, languageMap);
      setSelectedKey(nextLanguage);
      if (showRegionLanguageDropdown) {
        setConversationRegionSelection(
          buildConversationRegionSelection(
            countryCode,
            nextLanguage,
            countryOptionsMap,
            languageMap
          )
        );
      }
      SpeechUtility.destroyInstance();
      SpeechUtility.setLanguage(nextLanguage);
    };

    useEffect(() => {
      if (!enableConversationRegionSelection || !enableCountry) {
        setConversationRegionSelection(null);
        return;
      }
      const defaults = resolveDefaultCountryAndLanguage(conversationRegions);
      setSelectedCountryCode(defaults.countryCode);
      setSelectedKey(defaults.languageAzureCode);
      const languageMap = buildLanguageOptionsForRegion(
        conversationRegions.find((r) => r.country_code === defaults.countryCode)
      );
      setConversationRegionSelection(
        buildConversationRegionSelection(
          defaults.countryCode,
          defaults.languageAzureCode,
          countryOptionsMap,
          languageMap
        )
      );
      SpeechUtility.destroyInstance();
      SpeechUtility.setLanguage(defaults.languageAzureCode);
    }, [enableConversationRegionSelection, enableCountry, conversationRegions, countryOptionsMap]);

    useEffect(() => {
      if (!enableConversationRegionSelection || !showLegacyLanguageDropdown) return;
      setConversationRegionSelection(null);
      setSelectedCountryCode('');
      const legacyLanguages = SpeechUtility.translateLanguages;
      setSelectedKey((current) => {
        const validKey = resolveLanguageSelection(current, legacyLanguages);
        if (validKey !== current) {
          SpeechUtility.destroyInstance();
          SpeechUtility.setLanguage(validKey);
        }
        return validKey;
      });
    }, [enableConversationRegionSelection, showLegacyLanguageDropdown, advisorAbilities]);

    const handleSpeechAction = async () => {
      setIsSpeechMode?.(true);
      await (await SpeechUtility.getInstance(dispatch)).sttFromMic(isListening, setIsListening, inputValue, setInputValue);
    };

    const endVoiceAgentWebcall = useCallback(() => {
      cleanupVoiceAgentWebRtc(
        {
          peerConnectionRef: voiceAgentPcRef,
          audioRef: voiceAgentAudioRef,
          localStreamRef: voiceAgentLocalStreamRef,
        },
        () => {
          setIsVoiceAgentConnecting(false);
          setIsVoiceAgentConnected(false);
        }
      );
    }, []);

    const startVoiceAgentWebcall = useCallback(async () => {
      if (!token) {
        CtyShowToast(checkType.error, t('Authentication required. Please log in.'));
        return;
      }
      if (!voiceAgentAssistantId) {
        CtyShowToast(checkType.error, t('No voice agent is selected for this advisor. Choose one in advisor settings.'));
        return;
      }
      setIsVoiceAgentConnecting(true);
      setIsVoiceAgentConnected(false);
      await startVoiceAgentWebRtcSession({
        assistantId: voiceAgentAssistantId,
        token,
        t,
        postWebRtcSession,
        postOffer,
        refs: {
          peerConnectionRef: voiceAgentPcRef,
          audioRef: voiceAgentAudioRef,
          localStreamRef: voiceAgentLocalStreamRef,
        },
        logContext: '[CtyPromptInput]',
        onConnected: () => {
          setIsVoiceAgentConnecting(false);
          setIsVoiceAgentConnected(true);
        },
        onDisconnected: () => {
          setIsVoiceAgentConnecting(false);
          setIsVoiceAgentConnected(false);
        },
        onTranscriptLine: onVoiceAgentTranscriptLine,
      });
    }, [voiceAgentAssistantId, token, t, postWebRtcSession, postOffer, onVoiceAgentTranscriptLine]);

    const handleVoiceAgentMicPress = useCallback(() => {
      if (isVoiceAgentConnected || isVoiceAgentConnecting) {
        endVoiceAgentWebcall();
        return;
      }
      void startVoiceAgentWebcall();
    }, [isVoiceAgentConnected, isVoiceAgentConnecting, endVoiceAgentWebcall, startVoiceAgentWebcall]);

    useEffect(() => {
      onVoiceAgentConnectionChange?.(isVoiceAgentConnected);
    }, [isVoiceAgentConnected, onVoiceAgentConnectionChange]);

    useEffect(() => {
      if (!useVoiceAgentMic && (isVoiceAgentConnected || isVoiceAgentConnecting)) {
        endVoiceAgentWebcall();
      }
    }, [useVoiceAgentMic, isVoiceAgentConnected, isVoiceAgentConnecting, endVoiceAgentWebcall]);

    useEffect(() => {
      return () => {
        cleanupVoiceAgentWebRtc({
          peerConnectionRef: voiceAgentPcRef,
          audioRef: voiceAgentAudioRef,
          localStreamRef: voiceAgentLocalStreamRef,
        });
      };
    }, []);

    const handleLiveChat = () => {
      if (!document.getElementById("hs-script-loader")) {
        const script = document.createElement("script");
        script.src = `//js.hs-scripts.com/44503099.js`;
        script.id = "hs-script-loader";
        script.async = true;
        script.defer = true;

        // Add load event listener before appending the script
        script.addEventListener('load', () => {
          // Wait for HubSpot to initialize
          const checkHubSpot = setInterval(() => {
            // @ts-expect-error HubSpotConversations is not defined
            const HubSpotConversations = window.HubSpotConversations;
            if (HubSpotConversations) {
              clearInterval(checkHubSpot);
              setTimeout(() => {
                HubSpotConversations.widget.open();
              }, 1000);
              HubSpotConversations.on('widgetClosed', () => {
                HubSpotConversations.widget.remove();
              });
            }
          }, 100);
        });
        document.body.appendChild(script);
      } else {
        // @ts-expect-error HubSpotConversations is not defined
        const HubSpotConversations = window.HubSpotConversations;
        if (!HubSpotConversations.widget.status().loaded) {
          HubSpotConversations.widget.load();
        }
        HubSpotConversations.widget.open();
      }
    };

    const startContent = () => {
      const viewSearchIcon = !isSearchSpinnerLoading && enableSearch;
      return (
        <div className='relative'>
          {viewSearchIcon && <div className='flex items-center justify-start p-1'>
            <Icon icon="akar-icons:search" width="1.3em" height="1.3em" style={{ color: '#98A2B3' }} />
          </div>}
          {isSearchSpinnerLoading && <Spinner size="sm" />}
        </div>
      );
    };

    const optionalActionCount = useMemo(() => {
      let count = 0;
      if (enableLiveChat) count += 1;
      if (enableFile) count += 1;
      if (enableImage) count += 2; // camera + scanner
      if (showRegionLanguageDropdown) count += 2; // country + language
      else if (showLegacyLanguageDropdown) count += 1;
      if (enableSpeak && !inputDisabled) count += 1;
      return count;
    }, [
      enableLiveChat,
      enableFile,
      enableImage,
      showRegionLanguageDropdown,
      showLegacyLanguageDropdown,
      enableSpeak,
      inputDisabled,
    ]);

    const shouldStackOptionalActions =
      useStackedPromptLayout && (stackActionsBelowInput ?? optionalActionCount > 1);

    const useRadialMenuMode = shouldStackOptionalActions && optionalActionCount > 1;

    const radialActions = useMemo((): PromptRadialAction[] => {
      if (!useRadialMenuMode) return [];

      const actions: PromptRadialAction[] = [];
      if (enableLiveChat) {
        actions.push({
          id: 'live-chat',
          icon: 'fluent:people-chat-16-regular',
          label: t('Live Chat'),
          iconClassName: 'text-default-500',
          onPress: handleLiveChat,
        });
      }
      if (enableFile) {
        actions.push({
          id: 'file-upload',
          icon: 'clarity:attachment-line',
          label: t('Attach'),
          onPress: handleBrowseFiles,
          deferCloseMenu: true,
        });
      }
      if (enableImage) {
        actions.push({
          id: 'camera',
          icon: 'proicons:camera',
          label: t('Camera'),
          onPress: () => setCameraOpen(true),
          deferCloseMenu: true,
        });
        actions.push({
          id: 'scanner',
          icon: 'solar:scanner-broken',
          label: t('Scanner'),
          onPress: () => document.getElementById('image-upload')?.click(),
          deferCloseMenu: true,
        });
      }
      if (showRegionLanguageDropdown) {
        actions.push({
          id: 'country',
          icon: 'mdi:earth',
          label: t('Country'),
          onPress: () => setRadialSelectionPanel('country'),
        });
        actions.push({
          id: 'language',
          icon: 'mdi:translate',
          label: t('Language'),
          onPress: () => setRadialSelectionPanel('language'),
        });
      } else if (showLegacyLanguageDropdown) {
        actions.push({
          id: 'language',
          icon: 'mdi:translate',
          label: t('Language'),
          onPress: () => setRadialSelectionPanel('language'),
        });
      }
      if (enableSpeak && !inputDisabled) {
        if (useVoiceAgentMic) {
          actions.push({
            id: 'voice-agent',
            icon: 'mdi:microphone',
            label: isVoiceAgentConnected ? t('End voice conversation') : t('Talk to this voice agent'),
            iconClassName: isVoiceAgentConnected ? 'text-success' : 'text-default-500',
            isLoading: isVoiceAgentConnecting && !isVoiceAgentConnected,
            isActive: isVoiceAgentConnected,
            closeMenuOnPress: false,
            onPress: handleVoiceAgentMicPress,
          });
        } else {
          actions.push({
            id: 'speak',
            icon: isListening ? 'proicons:soundwave' : 'proicons:microphone-off',
            label: t('Speak'),
            iconClassName: isListening ? 'text-danger' : 'text-default-500',
            onPress: handleSpeechAction,
          });
        }
      }
      return actions;
    }, [
      useRadialMenuMode,
      enableLiveChat,
      enableFile,
      enableImage,
      showRegionLanguageDropdown,
      showLegacyLanguageDropdown,
      enableSpeak,
      inputDisabled,
      useVoiceAgentMic,
      isVoiceAgentConnected,
      isVoiceAgentConnecting,
      isListening,
      t,
      handleLiveChat,
      handleBrowseFiles,
      handleVoiceAgentMicPress,
      handleSpeechAction,
    ]);

    const keepPromptVisibleAboveKeyboard = useCallback(() => {
      const root = promptRootRef.current;
      if (!root) return;

      // Prefer scrolling the chat log container so the composer stays at the bottom.
      const scrollContainer = document.getElementById('scroll-container');
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
      }

      // Also bring the composer into the visual viewport (helps when the OS keyboard resizes the page).
      requestAnimationFrame(() => {
        root.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      });
    }, []);

    useEffect(() => {
      if (!useStackedPromptLayout) return;
      const viewport = window.visualViewport;
      if (!viewport) return;

      const onViewportChange = () => {
        if (promptRootRef.current?.contains(document.activeElement)) {
          keepPromptVisibleAboveKeyboard();
        }
      };

      viewport.addEventListener('resize', onViewportChange);
      viewport.addEventListener('scroll', onViewportChange);
      return () => {
        viewport.removeEventListener('resize', onViewportChange);
        viewport.removeEventListener('scroll', onViewportChange);
      };
    }, [useStackedPromptLayout, keepPromptVisibleAboveKeyboard]);

    const renderSendButton = () => {
      if (!enableSendBtn) return null;
      return (
        <CtyButton
          tooltipContent="Send message"
          id='chatsend-btn'
          isIconOnly
          isDisabled={isSocketLoading || inputDisabled || uploadedFiles.some(file => file.uploading)}
          radius="lg"
          size="sm"
          variant="light"
          onPress={() => handleSubmit()}
          className="shrink-0"
        >
          <Icon
            className={cn(
              "[&>path]:stroke-[2px]",
              inputValue?.trim() || (allowImageAttachment && uploadedFiles.some((f) => f.source_id))
                ? "text-primary-foreground "
                : "text-default-600",
            )}
            icon="icon-park-outline:send-one"
            width={20}
            color="DD3399"
          />
        </CtyButton>
      );
    };

    const renderOptionalActions = () => {
      let voiceAgentMicTooltip = t('Talk to this voice agent');
      if (isVoiceAgentConnected) voiceAgentMicTooltip = t('End voice conversation');
      else if (isVoiceAgentConnecting) voiceAgentMicTooltip = t('Connecting…');

      return (
        <>
          {enableLiveChat &&
            <CtyButton id='live-chat' isIconOnly tooltipContent="Live Chat" radius="full" size="sm" variant="light" onPress={handleLiveChat}>
              <Icon className="text-default-500" icon="fluent:people-chat-16-regular" width={20} />
            </CtyButton>
          }
          {enableFile &&
            <CtyButton id='file-upload' isIconOnly tooltipContent="File Upload" radius="full" size="sm" variant="light" onPress={handleBrowseFiles}>
              <Icon className="text-primary cursor-pointer " icon="clarity:attachment-line" width={20} height={20} />
            </CtyButton>
          }
          {enableImage && (
            <>
              <CtyButton id="camera-capture" isIconOnly tooltipContent="Camera" radius="full" size="sm" variant="light" onPress={() => setCameraOpen(true)}>
                <Icon className="text-primary cursor-pointer" icon="proicons:camera" width={22} height={22} />
              </CtyButton>
              <FileScanner actionsList={actionsList} setInputValue={setInputValue} />
            </>
          )}
          {showRegionLanguageDropdown && (
            <SearchableDropdown
              selectedKey={selectedCountryCode}
              className="max-h-[200px] overflow-y-auto"
              onSelectionChange={handleCountryDropDown}
              availableLanguages={countryOptionsMap}
              setLanguageOnSelection={false}
              searchPlaceholder={t('Search country...')}
              ariaLabel={t('Country selection')}
            />
          )}
          {showRegionLanguageDropdown && (
            <SearchableDropdown
              selectedKey={selectedKey}
              className="max-h-[200px] overflow-y-auto"
              onSelectionChange={handleDropDown}
              availableLanguages={regionLanguageMap}
              searchPlaceholder={t('Search language...')}
              ariaLabel={t('Language selection')}
            />
          )}
          {showLegacyLanguageDropdown && (
            <SearchableDropdown
              selectedKey={selectedKey}
              className="max-h-[200px] overflow-y-auto"
              onSelectionChange={(key) => handleDropDown(key)}
              availableLanguages={SpeechUtility.translateLanguages}
            />
          )}
          {enableSpeak && useVoiceAgentMic && !inputDisabled && (
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-visible">
                {isVoiceAgentConnecting && !isVoiceAgentConnected && (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute left-1/8 top-1/8 z-0 h-8 w-8 text-primary motion-reduce:animate-none animate-[spin_0.75s_linear_infinite]"
                  >
                    <svg className="h-full w-full" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="24" cy="24" r="20" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
                      <circle
                        cx="24"
                        cy="24"
                        r="20"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray="32 94"
                        transform="rotate(-90 24 24)"
                      />
                    </svg>
                  </div>
                )}
                {isVoiceAgentConnected && (
                  <>
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 scale-90 rounded-full border-2 border-success shadow-[0_0_12px_rgba(34,197,94,0.45)] motion-reduce:animate-none motion-reduce:opacity-40 animate-ping"
                    />
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 scale-90 rounded-full border-2 border-success/70 motion-reduce:animate-none motion-reduce:opacity-30 animate-ping [animation-delay:0.65s]"
                    />
                  </>
                )}
                <div className="relative z-[2]">
                  <CtyButton
                    id="prompt-voice-agent-mic"
                    isIconOnly
                    tooltipContent={voiceAgentMicTooltip}
                    radius="full"
                    size="sm"
                    variant="light"
                    isDisabled={!token}
                    onPress={handleVoiceAgentMicPress}
                    aria-busy={isVoiceAgentConnecting && !isVoiceAgentConnected}
                    aria-label={voiceAgentMicTooltip}
                  >
                    <Icon
                      className={isVoiceAgentConnected ? 'text-success' : 'text-default-500'}
                      icon="mdi:microphone"
                      width={22}
                    />
                  </CtyButton>
                </div>
              </div>
          )}
          {enableSpeak && !useVoiceAgentMic && !inputDisabled && (
            <CtyButton id={ID_CONSTANTS.chatSpeakBtn} isIconOnly tooltipContent={t('Speak')} radius="full" size="sm" variant="light" onPress={handleSpeechAction}>
              <div className="relative">
                {isListening && <Spinner className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2" />}
                <Icon className="text-default-500" icon={isListening ? 'proicons:soundwave' : 'proicons:microphone-off'} width={isListening ? 22 : 20} style={isListening ? { color: '#b51a1a' } : undefined} />
              </div>
            </CtyButton>
          )}
        </>
      );
    };

    const endContent = () => {
      if (useRadialMenuMode) {
        return (
          <div className="flex items-end justify-end gap-1 overflow-visible">
            <PromptActionsRadialMenu
              actions={radialActions}
              isDisabled={inputDisabled || isSocketLoading}
              hubLoading={isVoiceAgentConnecting && !isVoiceAgentConnected}
            />
            {renderSendButton()}
          </div>
        );
      }

      if (shouldStackOptionalActions) {
        return (
          <div className="flex items-end justify-end gap-1">
            {renderOptionalActions()}
            {renderSendButton()}
          </div>
        );
      }

      return (
        <div className="absolute right-0 h-full flex items-end justify-end gap-1">
          {renderOptionalActions()}
          {renderSendButton()}
        </div>
      );
    };
    const handleRemoveSelectedAdvisor = () => {
      setSelectedAdvisor(null); // Clear selection
      dispatch(updateCurrentChatMentionedEntities({})); // Clear Redux state
    };

    const handleRemoveSelectedAgent = () => {
      setSelectedAgent(null); // Clear selection
      dispatch(updateCurrentChatMentionedEntities({})); // Clear Redux state
    };
    return (
      <>
      {enableFile && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_FILE_INPUT_ACCEPT}
          className="hidden"
          onChange={(e) => uploadFileHandler(e)}
        />
      )}
      {useVoiceAgentMic && (
        <audio ref={voiceAgentAudioRef} className="hidden" autoPlay>
          <track kind="captions" />
        </audio>
      )}
      <div className="relative w-full" ref={promptRootRef}>
        {
          isVoiceAgentConnected ? (
            <div className="relative w-full rounded-medium border border-success/25 bg-gradient-to-r from-success/90 to-content1 p-4 shadow-sm">
              <div className="flex w-full items-center gap-4">
                <div className="relative flex h-12 w-12 shrink-0 items-center justify-center">
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-full border-2 border-success shadow-[0_0_16px_rgba(34,197,94,0.5)] motion-reduce:animate-none motion-reduce:opacity-45 animate-ping"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-full border-2 border-success/70 motion-reduce:animate-none motion-reduce:opacity-35 animate-ping [animation-delay:0.65s]"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -inset-1 rounded-full ring-2 ring-success/40 motion-reduce:animate-none motion-reduce:opacity-30 animate-pulse"
                  />
                  <Icon icon="proicons:soundwave" width={26} className="relative z-[2] text-success" />
                </div>
                <p className="min-w-0 flex-1 text-sm font-medium text-default-700">{t('Voice conversation in progress')}</p>
                <CtyButton size="sm" color="danger" variant="flat" onPress={endVoiceAgentWebcall}>
                  {t('End voice conversation')}
                </CtyButton>
              </div>
            </div>
          ) : isListening ? <MicrophoneWave handleSpeechAction={handleSpeechAction} /> :
            <div className="relative w-full">
              {showAdvisorDropdown && token && (
                <div ref={dropdownRef} className="absolute bottom-full mb-2 bg-content1 border shadow-md rounded-lg w-full max-h-40 overflow-auto z-50 p-2">
                  {/* Advisors Section */}
                  {filteredAdvisors.length > 0 && <div className="text-xs text-default-500 mb-2 px-2">@ Advisors</div>}
                  {filteredAdvisors.map((advisor) => (
                    <CtyButton
                      key={advisor.advisor_id}
                      className="p-2 cursor-pointer bg:white hover:bg-default-200 flex gap-2 items-center"
                      onPress={() => handleAdvisorSelect({ advisor_id: advisor.advisor_id, name: advisor.name })}
                      variant='light'
                    >
                      <img src={advisor.assets?.[0]?.s3_path || defaultAdvisorLogo} alt={advisor.name} width={20} height={20} className="rounded-full" />
                      <span className='font-semibold text-sm text-default-700'>{advisor.name}</span>
                    </CtyButton>
                  ))}

                  {/* Actions Section */}
                  {filteredActions.length > 0 && (
                    <>
                      <div className="text-xs text-default-500 mt-2 mb-2 px-2 border-t pt-2">@ Actions</div>
                      {filteredActions.map((action: any) => (
                        <CtyButton
                          key={action.id || action.name}
                          className="p-2 cursor-pointer bg:white hover:bg-default-200 flex gap-2 items-center"
                          onPress={() => handleActionSelect(action)}
                          variant='light'
                        >
                          <div className="w-5 h-5 rounded-full bg-warning/10 flex items-center justify-center text-warning">
                            <Icon icon="material-symbols:bolt" width="14" height="14" />
                          </div>
                          <span className='font-semibold text-sm text-default-700'>{action.name}</span>
                        </CtyButton>
                      ))}
                    </>
                  )}
                </div>
              )}

              {showAgentDropdown && token && (
                <div ref={dropdownRef} className="absolute bottom-full mb-2 bg-content1 border shadow-md rounded-lg w-full max-h-40 overflow-auto z-50 p-2">
                  <div className="text-xs text-default-500 mb-2 px-2">/ Agents</div>
                  {filteredAgents.map((agent) => (
                    <CtyButton
                      key={agent.aiworker_id}
                      className="p-2 cursor-pointer bg:white hover:bg-default-200 flex gap-2 items-center"
                      onPress={() => handleAgentSelect(agent)}
                      variant='light'
                    >
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                        <Icon icon="mingcute:robot-2-fill" width="12" height="12" className="text-white" />
                      </div>
                      <span className='font-semibold text-sm text-default-700'>{agent.name}</span>
                    </CtyButton>
                  ))}
                </div>
              )}

              {(selectedAdvisor || selectedAgent) && token && (
                <div className="flex gap-2 p-2">
                  {selectedAdvisor && (
                    <div className="bg-[whitesmoke] rounded-full px-3 py-1 flex justify-between items-center gap-2 w-full">
                      <div className="flex items-center gap-3">
                        <img src={allAdvisors.find((advisor: Advisor) => advisor.advisor_id === selectedAdvisor.advisor_id)?.assets?.[0]?.s3_path || defaultAdvisorLogo}
                          alt={selectedAdvisor.name} width={15} height={15} className="rounded-full" />
                        <span className="text-sm">@{selectedAdvisor.name}</span>
                      </div>
                      <Icon icon="zondicons:close-solid" width="20" height="20"
                        onClick={handleRemoveSelectedAdvisor} className="cursor-pointer" />
                    </div>
                  )}
                  {selectedAgent && (
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-primary rounded-full px-3 py-1 flex justify-between items-center gap-2 w-full">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                          <Icon icon="mingcute:robot-2-fill" width="10" height="10" className="text-white" />
                        </div>
                        <span className="text-sm font-medium text-primary">/{selectedAgent.name}</span>
                      </div>
                      <Icon icon="zondicons:close-solid" width="20" height="20"
                        onClick={handleRemoveSelectedAgent} className="cursor-pointer text-primary" />
                    </div>
                  )}
                </div>
              )}

              <AttachmentPreviewSection
                uploadedFiles={uploadedFiles}
                setUploadedFiles={setUploadedFiles}
                enableImage={allowImageAttachment}
              />
              {elementType === promptInputType.TEXTAREA &&
                <Textarea
                  ref={ref}
                  aria-label="Prompt"
                  className={`${inputHeight ?? 'min-h-[40px]'} `}
                  classNames={{
                    ...classNames,
                    label: cn("hidden", classNames?.label),
                    input: cn(
                      "py-0",
                      classNames?.input,
                    ),
                  }}
                  onChange={handleInputChange}
                  minRows={1}
                  value={inputValue}
                  onValueChange={setInputValue}
                  onKeyDown={(e) => (e.key === 'Enter' && !e.shiftKey) && handleSubmit(e)}
                  placeholder="Enter a prompt here"
                  radius="none"
                  endContent={endContent()}
                  startContent={startContent()}
                  variant="flat"
                  disabled={isSocketLoading || inputDisabled}
                  autoFocus={true}
                  {...props}
                  onFocus={(e) => {
                    props.onFocus?.(e);
                    if (useStackedPromptLayout) {
                      // Wait a beat for the mobile keyboard / visualViewport to settle.
                      window.setTimeout(keepPromptVisibleAboveKeyboard, 150);
                    }
                  }}
                  onPaste={(e) => {
                    props.onPaste?.(e);
                    handlePaste(e);
                  }}
                  onDragOver={(e) => {
                    props.onDragOver?.(e);
                    handleDragOver(e);
                  }}
                  onDrop={(e) => {
                    props.onDrop?.(e);
                    handleDrop(e);
                  }}
                />
              }
              {
                elementType === promptInputType.INPUT &&
                <Input
                  aria-label="Prompt"
                  size={props.size}
                  type="text"
                  endContent={endContent()}
                  startContent={startContent()}
                  placeholder={props.placeholder}
                  id={props.id}
                  onKeyDown={(e) => (e.key === 'Enter' && !e.shiftKey) && handleSubmit(e)}
                  value={inputValue}
                  onValueChange={setInputValue}
                  className={`min-w-sm ${inputHeight ?? 'min-h-[40px]'}`}
                  disabled={isSocketLoading || inputDisabled}
                  autoFocus={props.autoFocus ?? false}
                  onChange={handleInputChange}
                  onFocus={(e) => {
                    props.onFocus?.(e);
                    if (useStackedPromptLayout) {
                      window.setTimeout(keepPromptVisibleAboveKeyboard, 150);
                    }
                  }}
                  onPaste={(e) => {
                    props.onPaste?.(e);
                    handlePaste(e);
                  }}
                  onDragOver={(e) => {
                    props.onDragOver?.(e);
                    handleDragOver(e);
                  }}
                  onDrop={(e) => {
                    props.onDrop?.(e);
                    handleDrop(e);
                  }}
                />
              }
              {useRadialMenuMode && enableImage && (
                <div className="absolute left-[-9999px] h-px w-px overflow-hidden" aria-hidden="true">
                  <FileScanner actionsList={actionsList} setInputValue={setInputValue} />
                </div>
              )}
            </div>
        }
      </div>
      <Modal
        isOpen={radialSelectionPanel === 'country'}
        onOpenChange={(open) => !open && setRadialSelectionPanel(null)}
        placement="bottom-center"
        scrollBehavior="inside"
        size="sm"
      >
        <ModalContent className="mx-4 max-w-[260px]">
          {() => (
            <>
              <ModalHeader className="border-b border-divider py-3 text-sm">{t('Country selection')}</ModalHeader>
              <ModalBody className="px-3 py-3">
                <SearchableDropdown
                  selectedKey={selectedCountryCode}
                  className="max-h-[220px] w-full overflow-y-auto"
                  onSelectionChange={(key) => {
                    handleCountryDropDown(key);
                    setRadialSelectionPanel(null);
                  }}
                  availableLanguages={countryOptionsMap}
                  setLanguageOnSelection={false}
                  searchPlaceholder={t('Search country...')}
                  ariaLabel={t('Country selection')}
                />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal
        isOpen={radialSelectionPanel === 'language'}
        onOpenChange={(open) => !open && setRadialSelectionPanel(null)}
        placement="bottom-center"
        scrollBehavior="inside"
        size="sm"
      >
        <ModalContent className="mx-4 max-w-[260px]">
          {() => (
            <>
              <ModalHeader className="border-b border-divider py-3 text-sm">{t('Language selection')}</ModalHeader>
              <ModalBody className="px-3 py-3">
                <SearchableDropdown
                  selectedKey={selectedKey}
                  className="max-h-[220px] w-full overflow-y-auto"
                  onSelectionChange={(key) => {
                    handleDropDown(key);
                    setRadialSelectionPanel(null);
                  }}
                  availableLanguages={showRegionLanguageDropdown ? regionLanguageMap : SpeechUtility.translateLanguages}
                  searchPlaceholder={t('Search language...')}
                  ariaLabel={t('Language selection')}
                />
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
      {enableImage && (
        <Modal isOpen={isCameraOpen} onClose={closeCameraModal} size="md" isDismissable>
          <ModalContent>
            <ModalHeader className="flex flex-col gap-2">
              <span>{cameraMode === 'video' ? t('Capture video') : t('Capture photo')}</span>
              <div className="flex items-center gap-2">
                <CtyButton
                  size="sm"
                  variant={cameraMode === 'photo' ? 'solid' : 'flat'}
                  color={cameraMode === 'photo' ? 'primary' : 'default'}
                  onPress={() => !isRecordingVideo && !hasCapturedMedia && setCameraMode('photo')}
                  isDisabled={isRecordingVideo || hasCapturedMedia}
                >
                  {t('Photo')}
                </CtyButton>
                <CtyButton
                  size="sm"
                  variant={cameraMode === 'video' ? 'solid' : 'flat'}
                  color={cameraMode === 'video' ? 'primary' : 'default'}
                  onPress={() => !isRecordingVideo && !hasCapturedMedia && setCameraMode('video')}
                  isDisabled={isRecordingVideo || hasCapturedMedia}
                >
                  {t('Video')}
                </CtyButton>
              </div>
            </ModalHeader>
            <ModalBody className="flex flex-col items-center gap-4">
              <div className="relative rounded-lg overflow-hidden bg-default-100">
                {!hasCapturedMedia && (
                  <Webcam
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{ facingMode: 'environment' }}
                    width="auto"
                    height="auto"
                  />
                )}
                {cameraMode === 'photo' && hasCapturedMedia && capturedPhotoDataUrl && (
                  <img src={capturedPhotoDataUrl} alt={t('Captured photo')} className="max-h-[50vh] w-auto" />
                )}
                {cameraMode === 'video' && hasCapturedMedia && capturedVideoUrl && (
                  <video src={capturedVideoUrl} controls className="max-h-[50vh] w-auto bg-black">
                    <track kind="captions" />
                  </video>
                )}
              </div>
              {cameraMode === 'video' && isRecordingVideo && (
                <div className="text-sm text-danger font-semibold">{t('Recording')}: {formatRecordingTime(recordingSeconds)}</div>
              )}
              <PromptCameraModalFooter
                cameraMode={cameraMode}
                hasCapturedMedia={hasCapturedMedia}
                isRecordingVideo={isRecordingVideo}
                t={t}
                onRetake={handleRetakeCapture}
                onUpload={handleCapturedUpload}
                onCapturePhoto={handleCameraCapture}
                onToggleVideoRecord={handleVideoRecordToggle}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
    );
  },
);

export default CtyPromptInput;
export { CtyPromptInput };

CtyPromptInput.displayName = "CtyPromptInput";
